import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

// Image placeholder component
const ImagePlaceholder = ({ src, label, className = "" }) => {
  const [hasError, setHasError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  
  return (
    <div className={`relative rounded-3xl overflow-hidden ${className}`}>
      {!hasError && (
        <img 
          src={src} 
          alt={label}
          className={`w-full h-full object-contain ${loaded ? 'block' : 'hidden'}`}
          onLoad={() => setLoaded(true)}
          onError={() => setHasError(true)}
        />
      )}
      {(hasError || !loaded) && (
        <div className="w-full h-full bg-gray-200 rounded-3xl flex items-center justify-center text-gray-400 text-sm border-2 border-dashed border-gray-300">
          <span>{label}</span>
        </div>
      )}
    </div>
  );
};

// Card with CTA button component
const FeatureCard = ({ src, label, onCtaClick }) => {
  return (
    <div className="group relative rounded-3xl overflow-hidden hover:shadow-lg transition-all duration-300">
      {/* Image */}
      <div className="relative">
        <img 
          src={src} 
          alt={label}
          className="w-full h-auto object-contain"
          loading="lazy"
        />
      </div>
      
      {/* CTA Button - Hidden on mobile, visible on desktop hover */}
      <div className="absolute bottom-4 left-0 right-0 hidden md:flex justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <Button 
          onClick={onCtaClick}
          className="bg-[#fff301] hover:bg-[#e6dd01] text-black font-semibold px-6 py-3 rounded-full shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
          data-testid={`cta-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          Quero isto!
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const AboutSection = () => {
  // Function to scroll to pricing section
  const scrollToPlans = () => {
    const pricingSection = document.getElementById('planos');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="bg-white py-20 relative z-[50]">
      <div className="container mx-auto px-6 md:px-12">
        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-start mb-20">
          {/* Left Column - Title */}
          <div className="space-y-6 scroll-reveal">
            <p className="text-sm font-bold text-[#fff301] uppercase tracking-wider flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-[#fff301] rounded-full"></span>
              BRUNO RODRIGUES
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black leading-tight">
              O que é o<br />
              <span className="text-[#fff301]">acompanhamento<br />Bruno Rodrigues?</span>
            </h2>
            <p className="text-xl text-gray-700">
              O <strong>acompanhamento online Bruno Rodrigues</strong> é um serviço focado na tua <strong>saúde</strong>.
            </p>
          </div>

          {/* Right Column - Content */}
          <div className="space-y-6">
            <p className="text-lg text-gray-700 leading-relaxed">
              O acompanhamento online Bruno Rodrigues é um serviço completo, com foco em <strong>saúde, nutrição e movimento</strong>. É composto por:
            </p>
            <ul className="space-y-4 list-disc list-inside text-gray-700">
              <li>
                Um plano de <strong>nutrição</strong> personalizado com base em avaliação clínica individual, elaborado por nutricionistas credenciadas.
              </li>
              <li>
                Um plano de <strong>treino</strong> adaptado à tua condição física, objetivos e disponibilidade, com apoio contínuo de personal trainers certificados.
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Tudo acontece na nossa app, onde tens acesso aos teus planos, ajustes e contacto direto com a equipa.
            </p>
            {/* Highlight Box */}
            <div className="bg-[#f5f5f0] rounded-2xl p-6 mt-6">
              <p className="text-gray-800">
                <CheckCircle2 className="w-5 h-5 text-[#fff301] inline mr-2" />
                Acreditamos que cada corpo é <strong>único</strong>. Por isso, <strong>cada plano também é</strong>. Aqui, o teu progresso é <strong>acompanhado passo a passo</strong>.
              </p>
            </div>
          </div>
        </div>

        {/* Tabs Section - Vertical buttons */}
        <Tabs defaultValue="para-quem" className="w-full">
          <TabsList className="grid grid-cols-2 gap-4 mb-8 h-auto bg-transparent">
            <TabsTrigger 
              value="para-quem"
              className="rounded-2xl py-4 px-4 text-sm md:text-base font-semibold bg-[#f5f5f0] text-black data-[state=active]:bg-[#fff301] data-[state=active]:text-black transition-all whitespace-normal h-auto min-h-[70px] flex items-center justify-center text-center"
            >
              Para quem é este acompanhamento?
            </TabsTrigger>
            <TabsTrigger 
              value="como-ajudar"
              className="rounded-2xl py-4 px-4 text-sm md:text-base font-semibold bg-[#f5f5f0] text-black data-[state=active]:bg-[#fff301] data-[state=active]:text-black transition-all whitespace-normal h-auto min-h-[70px] flex items-center justify-center text-center"
            >
              Como te vamos ajudar?
            </TabsTrigger>
            <TabsTrigger 
              value="incluido"
              className="rounded-2xl py-4 px-4 text-sm md:text-base font-semibold bg-[#f5f5f0] text-black data-[state=active]:bg-[#fff301] data-[state=active]:text-black transition-all whitespace-normal h-auto min-h-[70px] flex items-center justify-center text-center"
            >
              O que está incluído no acompanhamento?
            </TabsTrigger>
            <TabsTrigger 
              value="pratica"
              className="rounded-2xl py-4 px-4 text-sm md:text-base font-semibold bg-[#f5f5f0] text-black data-[state=active]:bg-[#fff301] data-[state=active]:text-black transition-all whitespace-normal h-auto min-h-[70px] flex items-center justify-center text-center"
            >
              Como funciona na prática?
            </TabsTrigger>
          </TabsList>

          <TabsContent value="para-quem" className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <h3 className="text-3xl font-bold text-black mb-6">
              É para ti se queres cuidar da tua saúde com equilíbrio e consistência.
            </h3>
            <p className="text-lg text-gray-700 mb-6">Este acompanhamento foi pensado para quem:</p>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#fff301] flex-shrink-0 mt-1" />
                <span className="text-gray-700">Precisa de apoio alimentar para prevenir ou tratar uma condição de saúde</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#fff301] flex-shrink-0 mt-1" />
                <span className="text-gray-700">Procura orientação profissional para regular parâmetros clínicos (colesterol, glicemia, digestão, etc.)</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#fff301] flex-shrink-0 mt-1" />
                <span className="text-gray-700">Quer cuidar de si com consistência, acompanhamento real e base terapêutica</span>
              </li>
            </ul>
          </TabsContent>

          <TabsContent value="como-ajudar" className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <h3 className="text-3xl font-bold text-black mb-6">
              Vamos simplificar o caminho até à tua melhor versão
            </h3>
            <p className="text-lg text-gray-700 mb-6">O nosso compromisso é guiar-te com base em ciência, cuidado e objetivos reais:</p>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#fff301] flex-shrink-0 mt-1" />
                <span className="text-gray-700">Plano de nutrição personalizado com base em avaliação individual</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#fff301] flex-shrink-0 mt-1" />
                <span className="text-gray-700">Plano de treino adaptado aos teus objetivos e ao teu nível</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#fff301] flex-shrink-0 mt-1" />
                <span className="text-gray-700">Ajustes regulares sempre que precisares</span>
              </li>
            </ul>
          </TabsContent>

          <TabsContent value="incluido" className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <h3 className="text-2xl md:text-3xl font-bold text-black mb-6">
              O que está incluído <span className="text-[#fff301]">no acompanhamento?</span>
            </h3>
            {/* Grid de imagens - 6 cards em 2 colunas com CTA */}
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {/* Card 1 - Acesso à Plataforma Exclusiva */}
              <FeatureCard 
                src="/images/card-1.jpg"
                label="Acesso à Plataforma Exclusiva"
                onCtaClick={scrollToPlans}
              />
              {/* Card 2 - Plano de Treino 100% Personalizado */}
              <FeatureCard 
                src="/images/card-2.jpg"
                label="Plano de Treino"
                onCtaClick={scrollToPlans}
              />
              {/* Card 3 - Plano de Nutrição */}
              <FeatureCard 
                src="/images/card-3.jpg"
                label="Plano de Nutrição"
                onCtaClick={scrollToPlans}
              />
              {/* Card 4 - Vídeos de Execução */}
              <FeatureCard 
                src="/images/card-4.jpg"
                label="Vídeos de Execução"
                onCtaClick={scrollToPlans}
              />
              {/* Card 5 - Acompanhamento */}
              <FeatureCard 
                src="/images/card-5.jpg"
                label="Acompanhamento"
                onCtaClick={scrollToPlans}
              />
              {/* Card 6 - Motivação e Disciplina */}
              <FeatureCard 
                src="/images/card-6.jpg"
                label="Motivação e Disciplina"
                onCtaClick={scrollToPlans}
              />
            </div>
          </TabsContent>

          <TabsContent value="pratica" className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <h3 className="text-3xl font-bold text-black mb-6">
              Como funciona na prática?
            </h3>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#fff301] rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                  1
                </div>
                <div>
                  <h4 className="font-bold text-lg text-black mb-2">Inscrição e Avaliação</h4>
                  <p className="text-gray-700">Depois de te inscreveres, serás contactado pela nossa equipa para uma avaliação inicial completa.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#fff301] rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                  2
                </div>
                <div>
                  <h4 className="font-bold text-lg text-black mb-2">Plano Personalizado</h4>
                  <p className="text-gray-700">Recebes o teu plano de nutrição e treino personalizado na app, adaptado às tuas necessidades.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#fff301] rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                  3
                </div>
                <div>
                  <h4 className="font-bold text-lg text-black mb-2">Acompanhamento Contínuo</h4>
                  <p className="text-gray-700">Tens apoio direto com a equipa, ajustes regulares e acompanhamento do teu progresso.</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default AboutSection;